import { supabase } from '../lib/supabase';

// Removed direct Megsapp API URL and credential constants

// Function to fetch or refresh the token via the Edge Function
async function fetchOrRefreshToken(refreshToken?: string): Promise<{ token: string; refresh_token: string; expires_at: string }> {
  const { data, error } = await supabase.functions.invoke('megsapp-auth', {
    body: refreshToken ? { refresh_token: refreshToken } : {},
  });

  if (error) {
    console.error('Error invoking megsapp-auth function:', error);
    throw new Error(`Failed to authenticate with Megsapp: ${error.message}`);
  }

  if (!data || !data.token) {
      console.error('Invalid response from megsapp-auth function:', data);
      throw new Error('Failed to get token from authentication service.');
  }

  // Store the new/refreshed token details
  const { error: dbError } = await supabase
    .from('auth_tokens')
    .insert({
      token: data.token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
    });

  if (dbError) {
    // Log the error but proceed, as we might have a valid token anyway
    console.error('Failed to save Megsapp token to database:', dbError.message);
  }

  return data as { token: string; refresh_token: string; expires_at: string };
}

// This function is kept for potential manual login triggers if needed, but might become redundant.
// It now calls the internal fetchOrRefreshToken which calls the Edge Function.
export async function loginToMegsapp(): Promise<string> {
    console.log('Explicit login requested, invoking Edge Function...')
    const { token } = await fetchOrRefreshToken();
    return token;
}

// Main function to get a valid token
export async function getToken(): Promise<string> {
  // 1. Try to get the latest valid token from the database
  const { data: latestToken, error: fetchError } = await supabase
    .from('auth_tokens')
    .select('token, refresh_token, expires_at')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle(); // Use maybeSingle to handle no rows found gracefully

  if (fetchError) {
    console.error('Error fetching token from DB:', fetchError);
    // Proceed to login if fetch failed
  } else if (latestToken && new Date(latestToken.expires_at) > new Date()) {
    // 2. If valid token found, return it
    console.log('Using valid token from DB.');
    return latestToken.token;
  } else if (latestToken) {
    // 3. If token found but expired, try to refresh it
    try {
      console.log('Token expired, attempting refresh via Edge Function...');
      const { token: refreshedToken } = await fetchOrRefreshToken(latestToken.refresh_token);
      console.log('Token refreshed successfully.');
      return refreshedToken;
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      // Proceed to login if refresh failed
    }
  }

  // 4. If no token, expired token with failed refresh, or DB error, perform initial login
  console.log('No valid token found or refresh failed, performing initial login via Edge Function...');
  try {
      const { token: newToken } = await fetchOrRefreshToken();
      console.log('Initial login successful.');
      return newToken;
  } catch (loginError) {
      console.error('Initial login via Edge Function failed:', loginError);
      throw new Error('Unable to obtain Megsapp token. Please check Edge Function logs and configuration.');
  }
} 